import { FC, useContext, useState } from "react";
import { useWatch } from "react-hook-form";

import { useForm } from "capitalrx-components-2.0/lib/forms";
import { Body, Heading, Semibold, Title } from "capitalrx-components-2.0/lib/typography";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Form, RadioGroup, SubmitButton, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { Auth, createProviderManagementAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { BackLink, TinField, tinTrim } from "@/components";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { getThemeButton, npiValidator } from "@/utils";


interface ProviderCreateDataForm {
  readonly provider_type: ProviderManagement.ProviderTypeValue;
  readonly provider_tax_id_number: string;
  readonly tax_id_number_designation: ProviderManagement.DesignationTypeValue;
  readonly provider_billing_npi: string;
  readonly legal_business_name: string;
  readonly doing_business_as_name: string;
  readonly pharmacy_nabp_id: string;
  readonly pharmacy_chain_code: string;
}


interface ProviderCreateProps {
  refreshUser: Function;
  user: Auth.GetUserResponse;
}

export const ProviderCreate: FC<ProviderCreateProps> = ({ refreshUser, user }) => {
  const { setUserProvider, getAuthToken } = useContext(AuthenticationContext);
  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);

  const showAlert = useAlert();
  const form = useForm<ProviderCreateDataForm>({
    defaultValues: {
      provider_type: ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL,
      tax_id_number_designation: ProviderManagement.DESIGNATION_TYPE_VALUES.SSN,
    },
  });

  const { six, isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const StackOrShelf = isCompressed ? Stack : Shelf;
  const providerType = useWatch({
    control: form.control,
    name: "provider_type",
  });

  const [emailConflict, setEmailConflict] = useState<{ npi: string; tin: string } | null>(null);

  const handleProviderCreate = async (data: ProviderCreateDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.create);
    const request = {
      ...data,
      provider_tax_id_number: tinTrim(data.provider_tax_id_number),
    };
    try{
      const {
        provider_management_id,
        provider_enrollment_status,
        provider_billing_npi,
      } = await createProviderManagementAPI(request, getAuthToken());
      await setUserProvider(provider_management_id, provider_enrollment_status, provider_billing_npi);

      showAlert({
        type: "success",
        style: "banner",
        message: "Your account was successfully created",
      });

      logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.create_success);
      refreshUser();

    } catch (error) {
      console.error(error);

      const err = error as { response?: { status?: number; data?: { required_npi?: string; required_tin?: string } } };
      if (err.response?.status === 409 && err.response?.data) {
        const { required_npi, required_tin } = err.response.data;
        setEmailConflict({ npi: required_npi!, tin: required_tin! });
      } else {
        showAlert({
          type: "error",
          style: "banner",
          message: "Failed to save provider details. Please try again later.",
        });
      }
      // analytics log validate fail
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementCreate);
      return;
    }
  };

  return (
    <Stack spacing="large">
      <BackLink linkText="Home" />
      <Stack spacing="small">
        <Title testId="provider_create_title">Create a Provider Account</Title>
        <Stack spacing="none">
          <Body>No existing account was found for <Semibold>{user.email}</Semibold>.</Body>
          <Body>Enter the information below to create an account with this email address.</Body>
        </Stack>
      </Stack>

      {emailConflict && (
        <div style={{ backgroundColor: "FEF3C7", border: "1px solid #F59E0B", borderRadius: "4px", padding: "16px" }}>
          <Body>
            <Semibold>This email address is already associated with a different provider record.</Semibold>
          </Body>
          <Body>
            Please use the NPI/TIN combo from that record, or sign out and sign back in with a different email address.
          </Body>
        </div>
      )}

      <Form form={form} onSubmit={handleProviderCreate}>
        <Stack>
          <Stack spacing="large">
            <Stack>
              <Heading>Identification</Heading>
              <Grid>
                <Grid.Span columns={six}>
                  <RadioGroup.Field label="Provider Type" name="provider_type" testId="provider_type" orientation="horizontal">
                    <RadioGroup.Option value={ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL}>Medical, Dental, Vision</RadioGroup.Option>
                    <RadioGroup.Option value={ProviderManagement.PROVIDER_TYPE_VALUES.PHARMACY}>Pharmacy</RadioGroup.Option>
                  </RadioGroup.Field>
                </Grid.Span>
                <Grid.Break />

                <Grid.Spread columns={six}>
                  <TinField formName="provider_tax_id_number" designationName="tax_id_number_designation"/>
                  <RadioGroup.Field label="TIN Type" name="tax_id_number_designation" testId="tax_id_number_designation" orientation="horizontal">
                    <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.SSN}>SSN</RadioGroup.Option>
                    <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.EIN}>EIN</RadioGroup.Option>
                  </RadioGroup.Field>
                </Grid.Spread>
                <Grid.Break />

                <Grid.Span columns={six}>
                  <Textbox.Field
                    label="NPI (Billing)"
                    name="provider_billing_npi"
                    testId="provider_billing_npi"
                    validator={npiValidator}
                    maxLength={10}
                    required
                  />
                </Grid.Span>

              </Grid>
            </Stack>
            <Stack>
              <Heading>Business Details</Heading>
              <Grid>
                <Grid.Spread columns={six}>
                  <Textbox.Field
                    label="Provider Name (Legal Business Name)"
                    name="legal_business_name"
                    testId="legal_business_name"
                    required
                  />
                  <Textbox.Field label="Doing Business As (DBA) Name" name="doing_business_as_name" testId="doing_business_as_name" required />
                </Grid.Spread>
                <Grid.Break />
                {providerType === ProviderManagement.PROVIDER_TYPE_VALUES.PHARMACY && (
                  <Grid.Spread columns={six}>
                    <Textbox.Field label="NCPDP ID (NABP)" name="pharmacy_nabp_id" testId="pharmacy_nabp_id" required />
                    <Textbox.Field label="Relationship ID (Chain Code)" name="pharmacy_chain_code" testId="pharmacy_chain_code" />
                  </Grid.Spread>
                )}
              </Grid>
            </Stack>
          </Stack>
          <StackOrShelf spacing="small">
            <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="provider_create_form_submit_button">Create Account</SubmitButton>
            <Shelf.Spacer />
          </StackOrShelf>
        </Stack>
      </Form>
    </Stack>
  );
};
